'use client'

import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'

import { useToggle } from '@/shared/lib/hooks/useToggle'

import { ShowAllBlockCustomProps } from './types'

import cl from './ShowAllBlockCustom.module.scss'

/**
 * block for "Show More" button for large text
 * @param content - inner text
 * @param buttonText - button text
 * @param hasGradient - add gradient when text is collapsed
 * @param contentShortHeight - set height variant of collapsed text
 */

const ShowAllBlockCustom = ({
	buttonText,
	contentShortHeight = 'md',
	hasGradient = false,
	children,
	...props
}: ShowAllBlockCustomProps): JSX.Element => {
	const contentRef = useRef(null)
	const [showMore, toggleShowMore] = useToggle(false)
	const [isOverflowing, setIsOverflowing] = useState(false)

	const { activeText, defaultText } = buttonText
	const text = showMore ? activeText : defaultText
	const styleChildren = showMore
		? cl.showAllBlockCustom__content
		: clsx(
				cl.showAllBlockCustom__content,
				contentShortHeight === 'sm' ? cl.showAllBlockCustom__content_short_sm : cl.showAllBlockCustom__content_short_md,
				hasGradient && isOverflowing && cl.showAllBlockCustom__content_gradient
		  )

	const styleButton = showMore ? clsx(cl.showAllBlock__button, cl.showAllBlock__button_grey) : cl.showAllBlock__button

	useEffect(() => {
		if (contentRef.current) {
			const computedStyle = getComputedStyle(contentRef.current)
			const maxHeight = parseFloat(computedStyle.maxHeight)
			const scrollHeight = contentRef.current.scrollHeight
			setIsOverflowing(scrollHeight > maxHeight)
		}
	}, [])

	return (
		<div {...props} className={cl.showAllBlockCustom}>
			<div ref={contentRef} className={styleChildren}>
				{children}
			</div>

			{isOverflowing && (
				<button className={styleButton} onClick={toggleShowMore}>
					{text}
				</button>
			)}
		</div>
	)
}

export default ShowAllBlockCustom
